import {
  Button,
  Checkbox,
  CheckboxProvider,
  Heading,
  HeadingLevel,
  Radio,
  RadioGroup,
  RadioProvider,
  VisuallyHidden,
} from "@ariakit/react";
import { useEffect, useId, type ReactNode } from "react";
import { Link } from "wouter";
import { href } from "../App/href";
import { FailureView } from "../FailureView/FailureView";
import { LoadingView } from "../LoadingView/LoadingView";
import { CloseIcon } from "../PageHeaderButton/Icon";
import { useLocalWarband } from "../WarbandPage/useLocalWarband";
import {
  useWarbandContext,
  WarbandContext,
} from "../WarbandPage/useWarbandContext";
import { assertNever } from "./assertNever";
import { WarbandPrintable } from "./Printable";
import { info, layoutHint, page, panel, switcher } from "./style.css";
import {
  PrintContext,
  usePrintContext,
  usePrintSettings,
} from "./usePrintContext";

function RadioOption<const T extends string>(props: {
  label: string;
  value: T;
  onChange: (value: T) => void;
}) {
  const { label, value, onChange } = props;

  return (
    <label className={switcher.radioOption}>
      <VisuallyHidden>
        <Radio
          value={value}
          onChange={(event) => void onChange(event.target.value as T)}
        />
      </VisuallyHidden>

      {label}
    </label>
  );
}

function Switcher<const T extends string>(props: {
  label: string;
  defaultValue: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  children: ReactNode;
}) {
  const { label, options, defaultValue, children, onChange } = props;
  const labelId = useId();

  return (
    <RadioProvider defaultValue={defaultValue}>
      <div className={switcher.container}>
        <div id={labelId} className={info.heading}>
          {label}
        </div>

        <div className={switcher.content}>
          <RadioGroup className={switcher.radioGroup} aria-labelledby={labelId}>
            {options.map((option) => {
              return (
                <RadioOption
                  {...option}
                  key={option.value}
                  onChange={onChange}
                />
              );
            })}
          </RadioGroup>

          {children}
        </div>
      </div>
    </RadioProvider>
  );
}

function LayoutHint() {
  const print = usePrintContext();

  switch (print.layout) {
    case "SUMMARIES": {
      return (
        <div className={layoutHint.container}>
          <div className={layoutHint.text}>
            Shows fully resolved actions, defenses, and equipment per knight
            profile. All references in one place!
          </div>

          <div className={layoutHint.text}>
            <CheckboxProvider defaultValue={print.showKnights}>
              <label>
                <Checkbox
                  className={layoutHint.check}
                  onChange={(event) =>
                    void print.dispatch({
                      showKnights: event.target.checked,
                    })
                  }
                />
                Include space for tokens per knight on each summary card.
              </label>
            </CheckboxProvider>
          </div>
        </div>
      );
    }

    case "CARDS": {
      return (
        <div className={layoutHint.container}>
          <div className={layoutHint.text}>
            Shows all equipment per individual knight, plus a quick reference
            sheet with rules. Great for players who are already comfortable with
            the core rules.
          </div>
        </div>
      );
    }

    default: {
      assertNever(print.layout);
    }
  }
}

function PrintConfigPanel() {
  const print = usePrintContext();
  const warband = useWarbandContext();
  const warbandId = warband.id;

  return (
    <div className={panel.container}>
      <div className={panel.header}>
        <Heading className={panel.heading}>Print</Heading>

        <Link href={href("~/builder/w/:warbandId", { path: { warbandId } })}>
          <CloseIcon />
        </Link>
      </div>

      <HeadingLevel>
        <div className={panel.content}>
          <Switcher
            label="Layout"
            onChange={(layout) => void print.dispatch({ layout })}
            defaultValue={print.layout}
            options={[
              { value: "SUMMARIES", label: "Summaries" },
              { value: "CARDS", label: "Cards" },
            ]}
          >
            <LayoutHint />
          </Switcher>

          <div>
            <Heading className={info.heading}>Tips & Tricks</Heading>

            <p className={info.text}>
              <strong>Use Chrome, Edge, or Firefox browser.</strong> Safari does
              not reproduce the page as it was designed.
            </p>

            <p className={info.text}>
              <strong>Prefer printing on desktop.</strong> It is possible to use
              a phone/tablet, but the output is usually worse.
            </p>

            <p className={info.text}>
              <strong>Use system dialog to fine-tune your print.</strong> You
              should be able to change <em>font size</em> with scale, set{" "}
              <em>margins</em>, and switch off <em>headers</em>.
            </p>

            <p className={info.text}>
              <strong>Use Print to PDF</strong> to get a digital version of your
              printout.
            </p>
          </div>
        </div>
      </HeadingLevel>

      <div className={panel.footer}>
        <Button className={panel.print} onClick={() => void window.print()}>
          Print
        </Button>
      </div>
    </div>
  );
}

function useBodyBackgroundColor(bodyColor: string) {
  useEffect(() => {
    const style = window.document.body.style;
    const originalColor = style.backgroundColor;
    style.backgroundColor = bodyColor;

    return () => {
      style.backgroundColor = originalColor;
    };
  });
}

export function PrintPage() {
  const result = useLocalWarband();
  const [settings, dispatch] = usePrintSettings();
  useBodyBackgroundColor("white");

  if (result.isPending) {
    return <LoadingView />;
  }

  if (result.isFailure) {
    return <FailureView failure={result.failure} />;
  }

  return (
    <PrintContext value={{ ...settings, dispatch }}>
      <WarbandContext value={result.value}>
        <HeadingLevel>
          <div className={page.container}>
            <div className={page.preview}>
              <WarbandPrintable />
            </div>

            <PrintConfigPanel />
          </div>
        </HeadingLevel>
      </WarbandContext>
    </PrintContext>
  );
}
